/**
 * One customer on the supplier's Customers page.
 *
 * CR-8599: moved out of pages/Customers.tsx. The page now lists the customers
 * of every supplier site in view, so a card can belong to a site the user is
 * not acting as. It names the sites that supply the customer when more than
 * one site is in view, and shows the NDA control only when the acting site
 * supplies the customer (the NDA is signed by, and read for, that one site).
 */

import { useNavigate } from 'react-router-dom';
import { SupplierCustomer } from '../../lib/api';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import NDAStatusControl from '../onboarding/NDAStatusControl';
import { ArrowRight, Building2, Factory, Mail, MapPin, MessageSquare, Phone } from 'lucide-react';

interface CustomerCardProps {
  /** The customer, with the supplier sites in view that supply it. */
  customer: SupplierCustomer;
  /** CR-8681: the API serves the customer's profile to this user. */
  canOpenProfile: boolean;
  /** More than one supplier site is in view, so say which supply this customer. */
  showSuppliedBy: boolean;
  /** The site the user is acting as — the one the NDA is read and signed for. */
  actingSupplierId: number | null;
  /** Opens the Messages window for this customer. */
  onMessages: () => void;
}

/**
 * A customer card: contact details, the supplying sites, orders, the NDA
 * control and the Messages / View Orders actions.
 *
 * @param props - See {@link CustomerCardProps}
 * @returns The card
 */
export default function CustomerCard({
  customer, canOpenProfile, showSuppliedBy, actingSupplierId, onMessages,
}: CustomerCardProps) {
  const navigate = useNavigate();
  const suppliedBy = (customer.supplier_sites || []).map((site) => site.name).join(', ');
  const actingSiteSupplies = !customer.supplier_sites
    || customer.supplier_sites.some((site) => site.id === actingSupplierId);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Building2 className="h-5 w-5 text-primary" />
            </div>
            <div>
              {/* CR-6250: the name opens the customer's profile. A real
                  button, matching the Organisation Structure node, so it
                  is keyboard reachable. CR-8681: only for the users the
                  API serves that profile to; everyone else reads the
                  name as plain text. */}
              <CardTitle className="text-lg" data-testid={`customer-name-${customer.id}`}>
                {canOpenProfile ? (
                  <button
                    type="button"
                    className="text-left hover:text-primary hover:underline"
                    title={`Open ${customer.name}`}
                    onClick={() => navigate(`/companies/${customer.id}`)}
                  >
                    {customer.name}
                  </button>
                ) : (
                  customer.name
                )}
              </CardTitle>
              <Badge variant="outline" className="mt-1">
                Customer
              </Badge>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {customer.primary_email && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="h-4 w-4" />
            <span className="truncate">{customer.primary_email}</span>
          </div>
        )}
        {customer.primary_phone && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Phone className="h-4 w-4" />
            <span>{customer.primary_phone}</span>
          </div>
        )}
        {(customer.city || customer.country) && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="h-4 w-4" />
            <span>
              {[customer.city, customer.country].filter(Boolean).join(', ')}
            </span>
          </div>
        )}
        {showSuppliedBy && suppliedBy && (
          <div
            className="flex items-start gap-2 text-sm text-muted-foreground"
            data-testid={`customer-supplied-by-${customer.id}`}
          >
            <Factory className="h-4 w-4 mt-0.5 shrink-0" />
            <span>Supplied by {suppliedBy}</span>
          </div>
        )}

        <div className="pt-3 border-t flex items-center justify-between">
          <div className="text-sm flex items-center gap-3 flex-wrap">
            <span>
              <span className="text-muted-foreground">Orders: </span>
              <span className="font-medium">{customer.po_count || 0}</span>
            </span>
            {/* V12: sign the customer's NDA once, here, not per RFQ. Keyed by
                the acting site so a site switch re-reads that site's status. */}
            {actingSiteSupplies && (
              <NDAStatusControl
                key={actingSupplierId ?? 'home'}
                customerId={customer.id}
                customerName={customer.name}
              />
            )}
          </div>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={onMessages}
              className="text-primary"
            >
              <MessageSquare className="h-4 w-4 mr-1" />
              Messages
            </Button>
            <Button
              variant="ghost"
              size="sm"
              // CR-8600: the orders the card counts (every PO for this customer
              // site), so the list matches "Orders: N".
              onClick={() => navigate(`/purchasing?tab=po_headers&status_tab=all&customer=${customer.id}`)}
              className="text-primary"
            >
              View Orders
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
